/**
 * ExitToMap use case (contracts.md, save-and-exit-design): SaveProgress, setSession(null),
 * setScene('map'), render.update(mapView).
 * gameLoop.stop() is called in composition after this use case returns.
 *
 * saveProgress is now synchronous (localStorage.setItem completes before return),
 * so no async/await needed here.
 */

import type { RenderPort } from "../ports.js";
import { mapStateToView } from "../state-view.js";
import type { AppState } from "../types.js";
import type { GameSession } from "../../domain/session.js";
import type { MageProfile } from "../../domain/mage-profile.js";

export interface ExitToMapDeps {
  getSession(): GameSession | null;
  setSession(session: GameSession | null): void;
  getState(): Pick<AppState, "scene" | "mapProgress" | "selectedFieldId">;
  render: RenderPort;
  /** Synchronous save — localStorage.setItem completes before return. */
  saveProgress(): void;
  /** Optional getter for mage profile (for XP bar display after exit). */
  getMageProfile?: () => MageProfile;
}

export function createExitToMap(deps: ExitToMapDeps): () => void {
  return function exitToMap(): void {
    deps.saveProgress();
    deps.setSession(null);
    const state = deps.getState();
    const mapState: AppState = { ...state, scene: "map" };
    const mageProfile = deps.getMageProfile?.();
    deps.render.setScene("map");
    deps.render.update(mapStateToView(mapState, mageProfile));
  };
}
